import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Spin,
  Typography,
  Row,
  Col,
  Descriptions,
  Table,
  Statistic,
  Tag,
  Space,
  Button,
  App,
  Divider,
  Empty,
  Avatar,
  Tooltip,
  Modal,
  Dropdown,
  Input,
  DatePicker
} from 'antd';
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  TeamOutlined,
  EditOutlined,
  TrophyOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  PercentageOutlined,
  MobileOutlined,
  LaptopOutlined,
  GlobalOutlined,
  SendOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import userService from '../services/userService';
import campaignService from '../services/campaignService';
import businessUnitService from '../services/businessUnitService';
import PageHeader from './PageHeader';
import { BREADCRUMB_PATTERNS } from './BreadcrumbNav';

const { Title, Text } = Typography;

// Custom Sparkles Icon (AI 3-star look)
const SparklesIcon = ({ style = {} }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: '1em', height: '1em', ...style }}>
    <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z" />
    <path d="M19 14L19.75 16.25L22 17L19.75 17.75L19 20L18.25 17.75L16 17L18.25 16.25L19 14Z" opacity="0.7" />
    <path d="M5 14L5.5 15.5L7 16L5.5 16.5L5 18L4.5 16.5L3 16L4.5 15.5L5 14Z" opacity="0.5" />
  </svg>
);

const MemberDetails = () => {
  const { message } = App.useApp();
  const { id, businessUnitId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [member, setMember] = useState(null);
  const [businessUnit, setBusinessUnit] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [analytics, setAnalytics] = useState({
    totalCampaigns: 0,
    completedCampaigns: 0,
    inProgressCampaigns: 0,
    averageScore: 0,
    totalScore: 0,
    deliveryRate: 0,
    engagementRate: 0
  });
  
  // AI Insights state
  const [insightModalOpen, setInsightModalOpen] = useState(false);
  const [insightLoading, setInsightLoading] = useState(false);
  const [insightStatus, setInsightStatus] = useState('');
  const [insight, setInsight] = useState(null);
  const [insightPeriod, setInsightPeriod] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [followUpMessages, setFollowUpMessages] = useState([]);
  const [followUpInput, setFollowUpInput] = useState('');
  const [followUpLoading, setFollowUpLoading] = useState(false);
  const [customRangeModalOpen, setCustomRangeModalOpen] = useState(false);
  const [customDateRange, setCustomDateRange] = useState([null, null]);
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (id) {
      loadMemberDetails();
      loadMemberCampaigns();
    }
  }, [id]);

  const loadMemberDetails = async () => {
    try {
      setLoading(true);
      
      // Try the general user endpoint first (works for all user types)
      let result = await userService.getUserById(id);
      
      // If that fails, try the participant-specific endpoint
      if (!result.success || !result.data) {
        result = await userService.getParticipantById(id);
      }

      if (result.success && result.data) {
        const memberData = result.data;
        setMember(memberData);

        // Load business unit details if available
        if (memberData.businessUnit) {
          const buId = memberData.businessUnit._id || memberData.businessUnit;
          const buResult = await businessUnitService.getBusinessUnitById(buId);
          if (buResult?.data?.success) {
            setBusinessUnit(buResult.data.data);
          }
        }
      } else {
        message.error('Failed to load member details');
      }
    } catch (error) {
      console.error('Error loading member details:', error);
      message.error('Failed to load member details');
    } finally {
      setLoading(false);
    }
  };

  const loadMemberCampaigns = async () => {
    try {
      setLoadingCampaigns(true);
      
      // Use the new endpoint to get campaigns for this participant
      const result = await userService.getParticipantCampaigns(id);
      
      if (result.success && result.data) {
        setCampaigns(result.data);
        calculateAnalytics(result.data);
      } else {
        setCampaigns([]);
        calculateAnalytics([]);
      }
    } catch (error) {
      console.error('Error loading member campaigns:', error);
      // Don't show error message if it's just empty data
      setCampaigns([]);
      calculateAnalytics([]);
    } finally {
      setLoadingCampaigns(false);
    }
  };

  const calculateAnalytics = (campaignsList) => {
    const completed = campaignsList.filter(c => c.status === 'completed');
    const inProgress = campaignsList.filter(c => c.status === 'in-progress');
    const started = campaignsList.filter(c => c.startedAt);
    
    const totalScore = completed.reduce((sum, c) => sum + (c.score || 0), 0);
    const averageScore = completed.length > 0 ? totalScore / completed.length : 0;
    
    // Calculate delivery rate using target-based delivery (same as campaign reports)
    // Count targets where at least one channel was attempted (sent)
    const attempted = campaignsList.filter(c => 
      c.whatsappStatus || c.smsStatus
    );
    
    // Count targets where message was delivered via ANY channel
    const delivered = campaignsList.filter(c => {
      const whatsappDelivered = c.whatsappStatus === 'success';
      const smsDelivered = c.smsStatus === 'delivered';
      return whatsappDelivered || smsDelivered;
    });
    
    const deliveryRate = attempted.length > 0 
      ? (delivered.length / attempted.length) * 100 
      : 0;
    
    // Calculate engagement rate (campaigns that were started or completed)
    const engaged = started.length;
    const engagementRate = campaignsList.length > 0 
      ? (engaged / campaignsList.length) * 100 
      : 0;

    setAnalytics({
      totalCampaigns: campaignsList.length,
      completedCampaigns: completed.length,
      inProgressCampaigns: inProgress.length,
      averageScore: Math.round(averageScore * 10) / 10,
      totalScore: Math.round(totalScore),
      deliveryRate: Math.round(deliveryRate * 10) / 10,
      engagementRate: Math.round(engagementRate * 10) / 10
    });
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      completed: { color: 'success', icon: <CheckCircleOutlined />, text: 'Completed' },
      'in-progress': { color: 'processing', icon: <ClockCircleOutlined />, text: 'In Progress' },
      pending: { color: 'default', icon: <ClockCircleOutlined />, text: 'Pending' },
      expired: { color: 'error', icon: null, text: 'Expired' },
      excluded: { color: 'default', icon: null, text: 'Excluded' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  const campaignColumns = [
    {
      title: 'Campaign Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Button 
          type="link" 
          onClick={() => navigate(`/dashboard/campaigns/report/${record._id}`)}
          style={{ padding: 0 }}
        >
          {text}
        </Button>
      ),
      sorter: (a, b) => a.name.localeCompare(b.name)
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => getStatusTag(status),
      filters: [
        { text: 'Completed', value: 'completed' },
        { text: 'In Progress', value: 'in-progress' },
        { text: 'Pending', value: 'pending' },
        { text: 'Expired', value: 'expired' }
      ],
      onFilter: (value, record) => record.status === value
    },
    {
      title: 'Score',
      dataIndex: 'score',
      key: 'score',
      render: (score, record) => (
        <Space>
          {record.status === 'completed' ? (
            <Text strong style={{ color: score >= 70 ? '#52c41a' : score >= 50 ? '#faad14' : '#ff4d4f' }}>
              {score}%
            </Text>
          ) : (
            <Text type="secondary">-</Text>
          )}
        </Space>
      ),
      sorter: (a, b) => (a.score || 0) - (b.score || 0)
    },
    {
      title: 'Progress',
      key: 'progress',
      render: (_, record) => {
        if (record.totalQuestions > 0) {
          return (
            <Text type="secondary">
              {record.answeredQuestions} / {record.totalQuestions} questions
            </Text>
          );
        }
        return <Text type="secondary">-</Text>;
      }
    },
    {
      title: 'Completed At',
      dataIndex: 'completedAt',
      key: 'completedAt',
      render: (date) => date ? new Date(date).toLocaleDateString() : '-',
      sorter: (a, b) => {
        if (!a.completedAt) return 1;
        if (!b.completedAt) return -1;
        return new Date(a.completedAt) - new Date(b.completedAt);
      }
    },
    {
      title: 'Sent Via',
      dataIndex: 'deliveryChannel',
      key: 'deliveryChannel',
      render: (channel) => {
        if (!channel) return <Text type="secondary">-</Text>;
        const config = {
          whatsapp: { color: 'green', label: 'WhatsApp' },
          sms: { color: 'blue', label: 'SMS' },
          email: { color: 'purple', label: 'Email' }
        };
        const c = config[channel] || { color: 'default', label: channel };
        return <Tag color={c.color}>{c.label}</Tag>;
      },
      filters: [
        { text: 'WhatsApp', value: 'whatsapp' },
        { text: 'SMS', value: 'sms' },
        { text: 'Email', value: 'email' }
      ],
      onFilter: (value, record) => record.deliveryChannel === value
    },
    {
      title: 'Device',
      dataIndex: 'deviceSummary',
      key: 'deviceSummary',
      render: (summary, record) => {
        if (!summary) return <Text type="secondary">-</Text>;
        const isPhone = /iPhone|Android|Mobile/i.test(summary);
        return (
          <Tooltip title={record.deviceIp ? `IP: ${record.deviceIp}` : undefined}>
            <Space size={4}>
              {isPhone ? <MobileOutlined /> : <LaptopOutlined />}
              <Text style={{ fontSize: '13px' }}>{summary}</Text>
            </Space>
          </Tooltip>
        );
      },
      filters: campaigns
        .map(c => c.deviceSummary)
        .filter((v, i, a) => v && a.indexOf(v) === i)
        .map(d => ({ text: d, value: d })),
      onFilter: (value, record) => record.deviceSummary === value
    }
  ];

  const handleEdit = () => {
    if (businessUnitId) {
      navigate(`/dashboard/businessunit/${businessUnitId}/member/${id}/edit`);
    } else {
      navigate(`/dashboard/member/${id}/edit`);
    }
  };

  const handleGetAIInsights = async (period, dateRange = null) => {
    setInsightPeriod(period);
    setInsightModalOpen(true);
    setInsightLoading(true);
    setInsightStatus('');
    setInsight(null);
    setSessionId(null);
    setFollowUpMessages([]);
    setFollowUpInput('');

    let startDate = null;
    let endDate = null;
    if (period === 'custom' && dateRange) {
      startDate = dateRange[0].format('YYYY-MM-DD');
      endDate = dateRange[1].format('YYYY-MM-DD');
    }

    let accumulated = '';

    await userService.getParticipantAIInsights(id, {
      onStatus: (msg) => setInsightStatus(msg),
      onToken: (token) => {
        accumulated += token;
        setInsightLoading(false);
        setInsightStatus('');
        setInsight(prev => ({ ...(prev || {}), insight: accumulated }));
      },
      onDone: (meta) => {
        if (meta.sessionId) setSessionId(meta.sessionId);
        setInsight(prev => ({ ...(prev || {}), ...meta, insight: accumulated }));
        setInsightLoading(false);
      },
      onError: (err) => {
        message.error(err || 'Failed to get AI insights');
        setInsightModalOpen(false);
        setInsightLoading(false);
      }
    }, period, startDate, endDate);
  };

  const handleAskFollowUp = async () => {
    const question = followUpInput.trim();
    if (!question || !sessionId || followUpLoading) return;

    setFollowUpInput('');
    setFollowUpMessages(prev => [...prev, { role: 'user', content: question }]);
    setFollowUpLoading(true);
    setFollowUpMessages(prev => [...prev, { role: 'assistant', content: '' }]);

    await userService.askParticipantAIQuestion(id, sessionId, question, {
      onToken: (token) => {
        setFollowUpMessages(prev => {
          const msgs = [...prev];
          const last = msgs[msgs.length - 1];
          if (last?.role === 'assistant') msgs[msgs.length - 1] = { ...last, content: last.content + token };
          return msgs;
        });
        setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
      },
      onDone: () => {
        setFollowUpLoading(false);
        setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      },
      onError: (err) => {
        if (err === 'expired') {
          setSessionId(null);
          message.warning('Session expired. Please generate new insights to continue.');
        }
        setFollowUpMessages(prev => {
          const msgs = [...prev];
          const last = msgs[msgs.length - 1];
          if (last?.role === 'assistant' && !last.content) {
            msgs[msgs.length - 1] = { ...last, content: err === 'expired' ? 'Session expired.' : 'An error occurred. Please try again.' };
          }
          return msgs;
        });
        setFollowUpLoading(false);
      }
    });
  };

  const handleCloseInsightModal = () => {
    // Cleanup the AI session in the background
    if (sessionId) {
      userService.endParticipantAISession(id, sessionId);
    }
    setInsightModalOpen(false);
    setSessionId(null);
    setFollowUpMessages([]);
    setFollowUpInput('');
  };

  const periodMenuItems = [
    { key: 'thisMonth', label: 'This Month' },
    { key: 'lastMonth', label: 'Last Month' },
    { key: 'all', label: 'All Time' },
    { type: 'divider' },
    { key: 'custom', label: 'Custom Date Range...' }
  ];

  const getPeriodLabel = (period) => {
    if (period === 'custom' && customDateRange[0] && customDateRange[1]) {
      return `${customDateRange[0].format('D MMM YYYY')} – ${customDateRange[1].format('D MMM YYYY')}`;
    }
    const labels = { thisMonth: 'This Month', lastMonth: 'Last Month', all: 'All Time' };
    return labels[period] || 'All Time';
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" tip="Loading..." />
      </div>
    );
  }

  if (!member) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Empty description="Member not found" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      <PageHeader
        title={`${member.firstName || ''} ${member.lastName || ''}`}
        subtitle="Member Profile"
        breadcrumbPattern={
          businessUnitId && businessUnit
            ? BREADCRUMB_PATTERNS.businessUnitMemberDetails(
                businessUnit.name,
                `${member.firstName || ''} ${member.lastName || ''}`.trim() || 'Member'
              )
            : BREADCRUMB_PATTERNS.memberDetails(
                `${member.firstName || ''} ${member.lastName || ''}`.trim() || 'Member'
              )
        }
        extra={[
          <Button
            key="edit"
            type="primary"
            icon={<EditOutlined />}
            onClick={handleEdit}
          >
            Edit Member
          </Button>
        ]}
      />

      <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
        {/* Member Information Card */}
        <Col xs={24}>
          <Card bordered={false} bodyStyle={{ padding: '32px' }}>
            <Row gutter={32}>
              <Col xs={24} md={8} style={{ textAlign: 'center', borderRight: window.innerWidth > 768 ? '1px solid #f0f0f0' : 'none' }}>
                <Avatar 
                  size={100} 
                  style={{ 
                    backgroundColor: '#2E86AB',
                    fontSize: '36px',
                    marginBottom: '16px'
                  }}
                >
                  {(member.firstName || member.name || '').charAt(0)}
                  {(member.lastName || member.surname || '').charAt(0)}
                </Avatar>
                <Title level={3} style={{ marginBottom: '4px', marginTop: '16px' }}>
                  {member.firstName || member.name} {member.lastName || member.surname}
                </Title>
                <span 
                  style={{ 
                    fontSize: '13px', 
                    padding: '3px 10px',
                    fontWeight: '500',
                    color: '#2E86AB',
                    backgroundColor: 'rgba(46, 134, 171, 0.1)',
                    borderRadius: '4px',
                    display: 'inline-block',
                    marginTop: '8px'
                  }}
                >
                  {member.userType?.charAt(0).toUpperCase() + member.userType?.slice(1)}
                </span>
              </Col>

              <Col xs={24} md={8}>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <div>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                      EMAIL ADDRESS
                    </Text>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <MailOutlined style={{ color: '#2E86AB' }} />
                      <Text strong style={{ fontSize: '15px' }} copyable>{member.email}</Text>
                    </div>
                  </div>

                  <div>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                      PHONE NUMBER
                    </Text>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <PhoneOutlined style={{ color: '#2E86AB' }} />
                      <Text strong style={{ fontSize: '15px' }} copyable>
                        {member.phoneNumber 
                          ? (typeof member.phoneNumber === 'object' 
                              ? member.phoneNumber.full 
                              : member.phoneNumber)
                          : '-'}
                      </Text>
                    </div>
                  </div>

                  {businessUnit && (
                    <div>
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                        BUSINESS UNIT
                      </Text>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <TeamOutlined style={{ color: '#2E86AB' }} />
                        <Text strong style={{ fontSize: '15px' }}>{businessUnit.name}</Text>
                      </div>
                    </div>
                  )}
                </Space>
              </Col>

              <Col xs={24} md={8}>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  {member.department && (
                    <div>
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                        DEPARTMENT
                      </Text>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <UserOutlined style={{ color: '#2E86AB' }} />
                        <Text strong style={{ fontSize: '15px' }}>{member.department}</Text>
                      </div>
                    </div>
                  )}

                  {member.employeeId && (
                    <div>
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                        EMPLOYEE ID
                      </Text>
                      <Text strong style={{ fontSize: '15px' }}>{member.employeeId}</Text>
                    </div>
                  )}

                  <div>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                      ACCOUNT STATUS
                    </Text>
                    <Tag 
                      color={member.status === 'active' ? 'success' : 'default'}
                      style={{ fontSize: '13px', padding: '2px 10px' }}
                    >
                      {(member.status || 'active').charAt(0).toUpperCase() + (member.status || 'active').slice(1)}
                    </Tag>
                  </div>
                </Space>
              </Col>
            </Row>
          </Card>
        </Col>

        {/* Performance Summary Card */}
        <Col xs={24}>
          <Card title="Performance Summary" bordered={false} style={{ marginBottom: '24px' }}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic
                  title="Total Campaigns"
                  value={analytics.totalCampaigns}
                  prefix={<TrophyOutlined />}
                  valueStyle={{ color: '#2E86AB' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Completed"
                  value={analytics.completedCampaigns}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#2E86AB' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="In Progress"
                  value={analytics.inProgressCampaigns}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: '#2E86AB' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Avg Score"
                  value={analytics.averageScore}
                  suffix="%"
                  valueStyle={{ color: '#2E86AB' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Delivery Rate"
                  value={analytics.deliveryRate}
                  suffix="%"
                  valueStyle={{ color: '#2E86AB' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Engagement Rate"
                  value={analytics.engagementRate}
                  suffix="%"
                  valueStyle={{ color: '#2E86AB' }}
                />
              </Col>
            </Row>
          </Card>
        </Col>

      </Row>

      {/* Known Devices */}
      {member.knownDevices && member.knownDevices.length > 0 && (
        <Card
          title="Known Devices"
          bordered={false}
          style={{ marginBottom: '24px', marginTop: '24px' }}
        >
          <Table
            dataSource={[...member.knownDevices].sort((a, b) => new Date(b.lastSeenAt) - new Date(a.lastSeenAt))}
            rowKey={(r) => r.fingerprint || r._id}
            pagination={false}
            size="small"
            columns={[
              {
                title: 'Device',
                dataIndex: 'deviceSummary',
                key: 'device',
                render: (text, record) => {
                  const isPhone = /iPhone|Android|Mobile/i.test(text || record.userAgent || '');
                  return (
                    <Space>
                      {isPhone ? <MobileOutlined /> : <LaptopOutlined />}
                      <span>{text || 'Unknown device'}</span>
                    </Space>
                  );
                }
              },
              {
                title: 'IP Address',
                dataIndex: 'ipAddress',
                key: 'ip',
                render: (ip) => ip || '-'
              },
              {
                title: 'Last Seen',
                dataIndex: 'lastSeenAt',
                key: 'lastSeen',
                render: (val) => val ? new Date(val).toLocaleString() : '-'
              }
            ]}
          />
        </Card>
      )}

      <Divider />

      {/* Campaign History Table */}
      <Card
        title="Campaign History"
        bordered={false}
        style={{ marginTop: '24px' }}
      >
        <Table
          columns={campaignColumns}
          dataSource={campaigns}
          rowKey="_id"
          loading={loadingCampaigns}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} campaigns`
          }}
          locale={{
            emptyText: (
              <Empty
                description="This member has not been targeted in any campaigns yet"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )
          }}
        />
      </Card>

      {/* Floating AI Insights Button */}
      <Dropdown
        menu={{
          items: periodMenuItems,
          onClick: ({ key }) => {
            if (key === 'custom') {
              setCustomDateRange([null, null]);
              setCustomRangeModalOpen(true);
            } else {
              handleGetAIInsights(key);
            }
          }
        }}
        trigger={['click']}
        placement="topRight"
        disabled={analytics.completedCampaigns === 0}
      >
        <Tooltip
          title={analytics.completedCampaigns === 0 ? 'No completed quizzes yet' : 'AI Learning Insights'}
          placement="left"
        >
          <div
            style={{
              position: 'fixed',
              bottom: '32px',
              right: '32px',
              zIndex: 1000,
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: analytics.completedCampaigns === 0
                ? '#ccc'
                : 'linear-gradient(135deg, #2E86AB 0%, #1a5f7a 100%)',
              boxShadow: analytics.completedCampaigns === 0
                ? 'none'
                : '0 6px 20px rgba(46,134,171,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: analytics.completedCampaigns === 0 ? 'not-allowed' : 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
            }}
            onMouseEnter={e => {
              if (analytics.completedCampaigns > 0) {
                e.currentTarget.style.transform = 'scale(1.1)';
                e.currentTarget.style.boxShadow = '0 8px 28px rgba(46,134,171,0.65)';
              }
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = analytics.completedCampaigns === 0 ? 'none' : '0 6px 20px rgba(46,134,171,0.5)';
            }}
          >
            <SparklesIcon style={{ fontSize: '22px', color: '#fff' }} />
          </div>
        </Tooltip>
      </Dropdown>

      {/* AI Insights Modal */}
      <Modal
        title={
          <Space>
            <div style={{
              width: '28px',
              height: '28px',
              background: 'linear-gradient(135deg, #2E86AB 0%, #1a5f7a 100%)',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <SparklesIcon style={{ color: '#fff', fontSize: '14px' }} />
            </div>
            <span style={{ fontWeight: 600 }}>Learning Insights</span>
            <Tag color="#2E86AB" style={{ marginLeft: '8px' }}>{getPeriodLabel(insightPeriod)}</Tag>
          </Space>
        }
        open={insightModalOpen}
        onCancel={handleCloseInsightModal}
        footer={[
          <Button key="close" onClick={handleCloseInsightModal}>
            Close
          </Button>
        ]}
        width={720}
      >
        {insightLoading ? (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <div style={{
              width: '64px',
              height: '64px',
              background: 'linear-gradient(135deg, #2E86AB 0%, #1a5f7a 100%)',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px'
            }}>
              <SparklesIcon style={{ color: '#fff', fontSize: '28px' }} />
            </div>
            <Spin size="large" />
            <div style={{ marginTop: '16px', color: '#666', fontSize: '15px' }}>
              {insightStatus || 'Connecting...'}
            </div>
          </div>
        ) : insight ? (
          <div>
            {insight.hasData ? (
              <>
                <div style={{ marginBottom: '20px' }}>
                  {insight.insight.split('\n').filter(line => line.trim()).map((line, idx) => {
                    const lineUpper = line.toUpperCase();
                    const isSummary = lineUpper.includes('PERFORMANCE') || lineUpper.includes('SUMMARY');
                    const isPeer = lineUpper.includes('PEER') || lineUpper.includes('COMPARISON');
                    const isTiming = lineUpper.includes('TIMING') || lineUpper.includes('BEHAVIOUR');
                    const isTrend = lineUpper.includes('SCORE TREND') || lineUpper.includes('TREND');
                    const isStrength = lineUpper.includes('STRENGTH');
                    const isGap = lineUpper.includes('DEVELOPMENT') || lineUpper.includes('GAP');
                    const isAction = lineUpper.includes('RECOMMENDATION') || lineUpper.includes('ACTION');
                    
                    let bgColor = 'rgba(46,134,171,0.06)';
                    let leftBorderColor = '#2E86AB';
                    
                    if (isSummary) { 
                      bgColor = 'rgba(46,134,171,0.08)'; 
                      leftBorderColor = '#2E86AB';
                    }
                    if (isPeer) {
                      bgColor = 'rgba(114,46,209,0.08)';
                      leftBorderColor = '#722ed1';
                    }
                    if (isTiming) {
                      bgColor = 'rgba(47,84,235,0.08)';
                      leftBorderColor = '#2f54eb';
                    }
                    if (isTrend) {
                      bgColor = 'rgba(19,194,194,0.08)';
                      leftBorderColor = '#13c2c2';
                    }
                    if (isStrength) { 
                      bgColor = 'rgba(82,196,26,0.08)'; 
                      leftBorderColor = '#52c41a';
                    }
                    if (isGap) { 
                      bgColor = 'rgba(250,140,22,0.08)'; 
                      leftBorderColor = '#fa8c16';
                    }
                    if (isAction) { 
                      bgColor = 'rgba(46,134,171,0.10)'; 
                      leftBorderColor = '#2E86AB';
                    }
                    
                    return (
                      <div 
                        key={idx}
                        style={{ 
                          padding: '14px 16px', 
                          background: bgColor,
                          borderLeft: `4px solid ${leftBorderColor}`,
                          borderRadius: '0 8px 8px 0',
                          marginBottom: '12px',
                          lineHeight: '1.7',
                          fontSize: '14px',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                        }}
                      >
                        {line}
                      </div>
                    );
                  })}
                </div>
                {insight.stats && (
                  <div style={{ 
                    background: '#fafafa', 
                    borderRadius: '8px', 
                    padding: '16px',
                    marginTop: '8px'
                  }}>
                    <Row gutter={[16, 16]} justify="space-between">
                      <Col span={6} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '22px', fontWeight: 600, color: '#2E86AB' }}>
                          {insight.stats.totalCampaigns}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>Campaigns</div>
                      </Col>
                      <Col span={6} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '22px', fontWeight: 600, color: '#2E86AB' }}>
                          {insight.stats.completedQuizzes}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>Completed</div>
                      </Col>
                      <Col span={6} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '22px', fontWeight: 600, color: '#2E86AB' }}>
                          {insight.stats.averageScore}%
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>Avg Score</div>
                      </Col>
                      <Col span={6} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '22px', fontWeight: 600, color: '#2E86AB' }}>
                          {insight.stats.engagementRate || 0}%
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>Engagement</div>
                      </Col>
                    </Row>
                    <Divider style={{ margin: '12px 0' }} />
                    <Row gutter={[16, 8]} justify="space-between">
                      <Col span={8} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '18px', fontWeight: 600, color: '#2E86AB' }}>
                          {insight.stats.totalQuestions}
                        </div>
                        <div style={{ fontSize: '11px', color: '#666' }}>Questions</div>
                      </Col>
                      <Col span={8} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '18px', fontWeight: 600, color: '#2E86AB' }}>
                          {insight.stats.correctAnswers}
                        </div>
                        <div style={{ fontSize: '11px', color: '#666' }}>Correct</div>
                      </Col>
                      <Col span={8} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '18px', fontWeight: 600, color: '#2E86AB' }}>
                          {insight.stats.deliveryRate || 0}%
                        </div>
                        <div style={{ fontSize: '11px', color: '#666' }}>Delivery</div>
                      </Col>
                    </Row>
                  </div>
                )}

                {/* Follow-up conversation */}
                {followUpMessages.length > 0 && (
                  <div style={{ marginTop: '20px' }}>
                    <Divider style={{ margin: '0 0 16px 0' }}>
                      <span style={{ fontSize: '12px', color: '#999' }}>Follow-up Questions</span>
                    </Divider>
                    {followUpMessages.map((msg, idx) => (
                      <div key={idx} style={{
                        padding: '12px 16px',
                        background: msg.role === 'user' ? 'rgba(46,134,171,0.06)' : '#fafafa',
                        borderLeft: msg.role === 'user' ? '4px solid #2E86AB' : '4px solid #d9d9d9',
                        borderRadius: '0 8px 8px 0',
                        marginBottom: '10px',
                        lineHeight: '1.7',
                        fontSize: '14px'
                      }}>
                        {msg.role === 'user' ? (
                          <div>
                            <span style={{ fontSize: '12px', color: '#2E86AB', fontWeight: 600 }}>You asked:</span>
                            <div style={{ marginTop: '4px' }}>{msg.content}</div>
                          </div>
                        ) : (
                          <div>{msg.content}</div>
                        )}
                      </div>
                    ))}
                    {followUpLoading && (
                      <div style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <Spin size="small" />
                        <span style={{ marginLeft: '8px', color: '#999', fontSize: '13px' }}>Thinking...</span>
                      </div>
                    )}
                    <div ref={chatEndRef} />
                  </div>
                )}

                {/* Follow-up input */}
                {sessionId && (
                  <div style={{
                    marginTop: '16px',
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'flex-start'
                  }}>
                    <Input.TextArea
                      value={followUpInput}
                      onChange={(e) => setFollowUpInput(e.target.value)}
                      onPressEnter={(e) => {
                        if (!e.shiftKey) {
                          e.preventDefault();
                          handleAskFollowUp();
                        }
                      }}
                      placeholder="Ask a follow-up question about this learner..."
                      autoSize={{ minRows: 1, maxRows: 3 }}
                      disabled={followUpLoading}
                      style={{ flex: 1 }}
                    />
                    <Button
                      type="primary"
                      icon={<SendOutlined />}
                      onClick={handleAskFollowUp}
                      loading={followUpLoading}
                      disabled={!followUpInput.trim()}
                      style={{
                        background: 'linear-gradient(135deg, #2E86AB 0%, #1a5f7a 100%)',
                        border: 'none',
                        height: '36px'
                      }}
                    />
                  </div>
                )}
              </>
            ) : (
              <Empty 
                description={insight.insight || 'No data available for insights'} 
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </div>
        ) : null}
      </Modal>

      {/* Custom Date Range Modal */}
      <Modal
        open={customRangeModalOpen}
        onCancel={() => setCustomRangeModalOpen(false)}
        footer={null}
        width={380}
        centered
        title={<span style={{ fontWeight: 600 }}>Custom Date Range</span>}
      >
        <div style={{ padding: '8px 0 16px' }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: '16px', fontSize: '13px' }}>
            Select the date range to analyse {member?.name}'s quiz activity.
          </Text>
          <DatePicker.RangePicker
            style={{ width: '100%' }}
            size="large"
            value={customDateRange}
            onChange={(dates) => setCustomDateRange(dates)}
            disabledDate={(d) => d && d.valueOf() > Date.now()}
            format="D MMM YYYY"
            placeholder={['Start date', 'End date']}
            allowClear
          />
          <div style={{ display: 'flex', gap: '8px', marginTop: '20px', justifyContent: 'flex-end' }}>
            <Button onClick={() => setCustomRangeModalOpen(false)}>
              Cancel
            </Button>
            <Button
              type="primary"
              disabled={!customDateRange || !customDateRange[0] || !customDateRange[1]}
              onClick={() => {
                setCustomRangeModalOpen(false);
                handleGetAIInsights('custom', customDateRange);
              }}
              style={{
                background: 'linear-gradient(135deg, #2E86AB 0%, #1a5f7a 100%)',
                border: 'none'
              }}
            >
              <Space>
                <SparklesIcon />
                Analyse
              </Space>
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default MemberDetails;
